import { logger } from "@/lib/logger";
/**
 * Progressive Web App service-worker registration wrapper.
 *
 * Rules from the Lovable PWA skill:
 * - Register ONLY in production (import.meta.env.PROD).
 * - NEVER register inside an iframe.
 * - NEVER register on Lovable preview / dev hostnames.
 * - Support ?sw=off kill-switch.
 * - Unregister any stale /sw.js registrations when in a refused context.
 */

const SW_PATH = "/sw.js";

function isAllowedHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  const isPreview =
    h.startsWith("id-preview--") ||
    h.startsWith("preview--") ||
    h === "lovableproject.com" ||
    h.endsWith(".lovableproject.com") ||
    h === "lovableproject-dev.com" ||
    h.endsWith(".lovableproject-dev.com") ||
    h === "beta.lovable.dev" ||
    h.endsWith(".beta.lovable.dev");
  return !isPreview;
}

function isIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function hasKillSwitch(): boolean {
  return typeof window !== "undefined" && window.location.search.includes("sw=off");
}

async function unregisterStale(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs
        .filter((r) => r.scope.endsWith(SW_PATH.replace(/\/sw\.js$/, "/")) || r.scope.endsWith("/"))
        .map((r) => r.unregister())
    );
  } catch {
    /* ignore */
  }
}

export async function registerPWA(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const shouldRegister =
    import.meta.env.PROD && isAllowedHost() && !isIframe() && !hasKillSwitch();

  if (!shouldRegister) {
    await unregisterStale();
    return;
  }

  try {
    await navigator.serviceWorker.register(SW_PATH, {
      scope: "/",
      updateViaCache: "imports",
    });
  } catch (err) {
    logger.warn("[PWA] Service worker registration failed:", err);
  }
}
